import React, { useState, useEffect } from 'react';
import './ExamMasterPage.css';
import { fetchCategories } from '../../../../api/categoryApi';
import { fetchExamTypes, createExam, updateExam } from '../../../../api/examApi';
import { fetchTrainings } from '../../../../api/trainingApi';  // New API import
import { toast } from 'react-toastify';
import Select from 'react-select';  // Import React-Select

const ExamMasterPage = ({ examToEdit, onClose }) => {
    const [examName, setExamName] = useState('');
    const [examType, setExamType] = useState('');
    const [allCategories, setAllCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState('');
    const [isTimed, setIsTimed] = useState(false);
    const [durationMinutes, setDurationMinutes] = useState('');
    const [examTypes, setExamTypes] = useState([]);
    const [passingScore, setPassingScore] = useState('');
    const [trainingRequired, setTrainingRequired] = useState(false);
    const [mandatoryTrainingDuration, setMandatoryTrainingDuration] = useState('');
    const [trainings, setTrainings] = useState([]);
    const [selectedTraining, setSelectedTraining] = useState(null);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [categoryData, examTypeData] = await Promise.all([
                    fetchCategories(),
                    fetchExamTypes(),
                ]);
                setAllCategories(categoryData);
                setExamTypes(examTypeData);
    
                if (examToEdit) {
                    // Only set the state if examToEdit is not null
                    setExamName(examToEdit.examName || '');
                    setExamType(examToEdit.examType?.examTypeDesc || '');
                    setTotalQuestions(examToEdit.numberOfQuestions?.toString() || '');
                    setIsTimed(examToEdit.isTimed === 1);
                    setDurationMinutes(examToEdit.durationMinutes?.toString() || '');
                    setPassingScore(examToEdit.passingScore?.toString() || '');
                    setTrainingRequired(examToEdit.trainingRequired === 1);
                    setMandatoryTrainingDuration(examToEdit.mandatoryTrainingDuration?.toString() || '');
    
                    const filtered = categoryData.filter(
                        (category) => category.examType.examTypeDesc === examToEdit.examType?.examTypeDesc
                    );
                    setFilteredCategories(filtered.map(category => {
                        const existingCategory = examToEdit.examCategory?.find(
                            ec => ec.category.categoryId === category.categoryId
                        );
                        return {
                            ...category,
                            selected: !!existingCategory,
                            weight: existingCategory ? existingCategory.percentageWeightage.toString() : ''
                        };
                    }));
                }
            } catch (error) {
                console.error('Error loading data:', error);
                toast.error('Please try again or contact Finhaat.', { icon: '❌' });
            }
        };
    
        loadInitialData();
    }, [examToEdit]);
    

    // Fetch trainings when trainingRequired checkbox is checked
    useEffect(() => {
        const loadTrainings = async () => {
            if (trainingRequired) {
                try {
                    const trainingData = await fetchTrainings();
                    setTrainings(trainingData.map(training => ({
                        value: training.trainingId,
                        label: training.trainingName,
                    })));
                } catch (error) {
                    console.error('Error fetching trainings:', error);
                    toast.error('Could not load trainings.', { icon: '❌' });
                }
            }
        };

        loadTrainings();
    }, [trainingRequired]);

    const handleExamNameChange = (e) => setExamName(e.target.value);
    const handleExamTypeChange = (e) => {
        const selectedExamType = e.target.value;
        setExamType(selectedExamType);
        const filtered = allCategories.filter(
            (category) => category.examType.examTypeDesc === selectedExamType
        );
        setFilteredCategories(filtered.map(category => ({ ...category, selected: false, weight: '' })));
    };

    const handleCategoryChange = (index) => {
        const updatedCategories = [...filteredCategories];
        updatedCategories[index].selected = !updatedCategories[index].selected;
        setFilteredCategories(updatedCategories);
    };

    const handleWeightChange = (e, index) => {
        const { value } = e.target;
        const updatedCategories = [...filteredCategories];
        updatedCategories[index].weight = value;
        setFilteredCategories(updatedCategories);
    };

    const handleTotalQuestionsChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setTotalQuestions(value);
        }
    };

    const handleIsTimedChange = () => setIsTimed(prev => !prev);
    const handleDurationChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setDurationMinutes(value);
        }
    };

    const handlePassingScoreChange = (e) => setPassingScore(e.target.value);
    const handleTrainingRequiredChange = () => setTrainingRequired(prev => !prev);
    const handleMandatoryTrainingDurationChange = (e) => setMandatoryTrainingDuration(e.target.value);
    const handleTrainingSelectChange = (selectedOption) => setSelectedTraining(selectedOption);

    const handleSubmit = async () => {
        const selectedCategories = filteredCategories.filter(category => category.selected);
        const totalWeight = selectedCategories.reduce((acc, category) => acc + Number(category.weight || 0), 0);

        if (totalWeight !== 100) {
            toast.error('Total weight of selected categories must be 100%', { icon: '⚖️' });
            return;
        }

        const examData = {
            examId: examToEdit ? examToEdit.examId : 0,
            examType: examTypes.find(type => type.examTypeDesc === examType),
            examName: examName,
            numberOfQuestions: parseInt(totalQuestions) || 0,
            isTimed: isTimed ? 1 : 0,
            durationMinutes: isTimed ? (parseInt(durationMinutes) || 0) : 0,
            passingScore: parseInt(passingScore) || 0,
            trainingRequired: trainingRequired ? 1 : 0,
            mandatoryTrainingDuration: trainingRequired ? parseInt(mandatoryTrainingDuration) || 0 : 0,
            trainingId: trainingRequired && selectedTraining ? selectedTraining.value : 0,
            examCategory: selectedCategories.map(category => ({
                category: { categoryId: category.categoryId, categoryName: category.categoryName },
                percentageWeightage: parseInt(category.weight) || 0
            }))
        };

        try {
            if (examToEdit) {
                await updateExam(examData);
                toast.success('Exam updated successfully!', { icon: '✏️' });
            } else {
                await createExam(examData);
                toast.success('Exam created successfully!', { icon: '🆕' });
            }
            resetForm();
            if (onClose) onClose();
        } catch (error) {
            console.error('Error saving exam:', error);
            toast.error('Please try again or contact Finhaat Team.', { icon: '❌' });
        }
    };

    const resetForm = () => {
        setExamName('');
        setExamType('');
        setTotalQuestions('');
        setIsTimed(false);
        setDurationMinutes('');
        setPassingScore('');
        setTrainingRequired(false);
        setMandatoryTrainingDuration('');
        setFilteredCategories([]);
        setSelectedTraining(null);
    };

    return (
        <div className="exam-master-page-container">
            <div className="exam-master-page">
                <div className="form-container">
                    <div className="form-group">
                        <label>Exam Name:</label>
                        <input
                            type="text"
                            value={examName}
                            onChange={handleExamNameChange}
                            className="form-control"
                            placeholder="Enter Exam Name"
                        />
                    </div>

                    <div className="form-group">
                        <label>Exam Type:</label>
                        <select value={examType} onChange={handleExamTypeChange} className="form-control">
                            <option value="">Select Exam Type</option>
                            {examTypes.map((type) => (
                                <option key={type.examTypeId} value={type.examTypeDesc}>
                                    {type.examTypeDesc}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Total Questions:</label>
                        <input
                            type="number"
                            value={totalQuestions}
                            onChange={handleTotalQuestionsChange}
                            className="form-control"
                            min="0"
                            placeholder="Enter Total Questions"
                        />
                    </div>

                    <div className="form-group timed-exam-group">
                        <label>Exam Timer:</label>
                        <input
                            type="checkbox"
                            checked={isTimed}
                            onChange={handleIsTimedChange}
                            className="timed-exam-checkbox"
                        />
                    </div>

                    {isTimed && (
                        <div className="form-group duration-form-group">
                            <label>Duration (Minutes):</label>
                            <input
                                type="number"
                                value={durationMinutes}
                                onChange={handleDurationChange}
                                className="form-control"
                                min="0"
                                placeholder="Enter Duration in Minutes"
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <label>Passing Score:</label>
                        <input
                            type="number"
                            value={passingScore}
                            onChange={handlePassingScoreChange}
                            className="form-control"
                            min="0"
                            placeholder="Enter Passing Score"
                        />
                    </div>

                    <div className="form-group training-exam-group">
                        <label>Training Required:</label>
                        <input
                            type="checkbox"
                            checked={trainingRequired}
                            onChange={handleTrainingRequiredChange}
                            className="training-required-checkbox"
                        />
                    </div>

                    {trainingRequired && (
                        <div className="form-group">
                            <label>Select Training:</label>
                            <Select
                                value={selectedTraining}
                                onChange={handleTrainingSelectChange}
                                options={trainings}
                                className="react-select"
                            />
                        </div>
                    )}

                    {trainingRequired && (
                        <div className="form-group">
                            <label>Mandatory Training Duration (Minutes):</label>
                            <input
                                type="number"
                                value={mandatoryTrainingDuration}
                                onChange={handleMandatoryTrainingDurationChange}
                                className="form-control"
                                min="0"
                                placeholder="Enter Duration in Minutes"
                            />
                        </div>
                    )}

                    <button onClick={handleSubmit} className="submit-btn">
                        {examToEdit ? 'Update' : 'Submit'}
                    </button>

                    {onClose && (
                        <button onClick={onClose} className="cancel-btn">
                            Cancel
                        </button>
                    )}
                </div>

                <div className="categories-container">
                    <h4 className="categories-container-h4">🎗️ Categories</h4>
                    {filteredCategories.length > 0 && (
                        <div className="categories-list">
                            {filteredCategories.map((category, index) => (
                                <div key={index} className="category-item">
                                    <div className="checkbox-name">
                                        <input
                                            type="checkbox"
                                            checked={category.selected || false}
                                            onChange={() => handleCategoryChange(index)}
                                            className="category-checkbox"
                                        />
                                        <span className="category-name">{category.categoryName}</span>
                                    </div>
                                    {category.selected && (
                                        <input
                                            type="text"
                                            value={category.weight || ''}
                                            onChange={(e) => handleWeightChange(e, index)}
                                            placeholder="(%)"
                                            className="weight-input"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExamMasterPage;
